import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import type {
  IntakeFormConfiguration,
  OrganizationEnrollmentsResponse,
  OrganizationEnrollServicesRequest,
} from '../../types';
import { cardSx, overlineSx, pillSx, wf } from '../shell/tokens';

import { agreementTitlesForPlan } from './IntakeSetupAgreementsPage';

export type IntakeServiceTier = NonNullable<OrganizationEnrollmentsResponse['billing_tier']>;

/** Service choices in the practice setup flow. */
export type IntakeSetupService =
  | IntakeFormConfiguration['products'][number]
  | NonNullable<OrganizationEnrollServicesRequest['addons']>[number];

export const INTAKE_SERVICE_TIER_LABELS: Record<IntakeServiceTier, string> = {
  managed: 'Managed services',
  self_pay: 'JotBill Self-Pay',
};

const tiers: { id: IntakeServiceTier; description: string }[] = [
  {
    id: 'managed',
    description: 'Pick any combination of JotBill, JotAssure and JotCred below.',
  },
  {
    id: 'self_pay',
    description:
      'You bill through JotPsych yourself. We connect you to payers and give you the tooling. No managed services, so no MSA.',
  },
];

export const INTAKE_SETUP_SERVICES: {
  id: IntakeSetupService;
  title: string;
  shortName: string;
  description: string;
  note: string;
  /** Mirrors the Order Form: JotAssure only exists on top of JotBill. */
  requires?: IntakeSetupService;
}[] = [
  {
    id: 'jotbill',
    title: 'JotBill Managed Billing',
    shortName: 'JotBill',
    description: 'One flat rate on what insurance pays. We submit, work, and collect.',
    note: 'Under the Master Services Agreement',
  },
  {
    id: 'jotassure',
    title: 'JotAssure Certified Claim Program',
    shortName: 'JotAssure',
    description: 'Same-week payment on certified claims. Requires JotBill.',
    note: 'Under the Master Services Agreement',
    requires: 'jotbill',
  },
  {
    id: 'jotcred',
    title: 'JotCred Credentialing',
    shortName: 'JotCred',
    description: 'We credential each clinician with your payers.',
    note: 'Covered by the BAA · service terms to follow',
  },
];

export interface IntakeServicePlan {
  /** "JotBill + JotCred", "JotBill Self-Pay + JotCred", or "No services selected". */
  name: string;
  agreements: string[];
  /** Self-Pay always has a line; Managed needs at least one service. */
  hasServices: boolean;
}

export function describeServicePlan(
  tier: IntakeServiceTier,
  services: readonly IntakeSetupService[],
): IntakeServicePlan {
  if (tier === 'self_pay') {
    const cred = services.includes('jotcred');
    return {
      name: cred ? 'JotBill Self-Pay + JotCred' : 'JotBill Self-Pay',
      agreements: ['Business Associate Agreement'],
      hasServices: true,
    };
  }
  const picked = INTAKE_SETUP_SERVICES.filter(service => services.includes(service.id));
  const needsMsa = services.includes('jotbill');
  return {
    name: picked.length
      ? picked.map(service => service.shortName).join(' + ')
      : 'No services selected',
    agreements: needsMsa
      ? ['Order Form', 'Master Services Agreement', 'Business Associate Agreement']
      : ['Business Associate Agreement'],
    hasServices: picked.length > 0,
  };
}

const SELF_PAY_ADDON = {
  id: 'jotcred' as const,
  title: 'JotCred Credentialing',
  description: 'We credential each clinician with your payers while you keep billing yourself.',
  note: 'Covered by the BAA · service terms to follow',
  chip: undefined as string | undefined,
};

function OptionCard({
  role,
  checked,
  disabled = false,
  title,
  description,
  note,
  chip,
  onActivate,
}: {
  role: 'radio' | 'checkbox';
  checked: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  note?: string;
  chip?: string;
  onActivate: () => void;
}) {
  return (
    <Box
      role={role}
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onActivate}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
      sx={{
        ...cardSx,
        padding: '16px 18px',
        display: 'flex',
        gap: '14px',
        alignItems: role === 'radio' ? 'stretch' : 'flex-start',
        flexDirection: role === 'radio' ? 'column' : 'row',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
        borderColor: checked ? wf.blue : wf.line,
        boxShadow: checked ? `0 0 0 1px ${wf.blue} inset` : 'none',
        backgroundColor: wf.surface,
        '&:focus-visible': { outline: `3px solid ${wf.blue}`, outlineOffset: 2 },
      }}
    >
      {role === 'radio' ? (
        <Stack
          direction="row"
          spacing={1.25}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              flex: '0 0 18px',
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: wf.surface,
              border: checked ? `6px solid ${wf.blue}` : `1.5px solid #b8b8c8`,
            }}
          />
        </Stack>
      ) : (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            mt: '2px',
            flex: '0 0 18px',
            width: 18,
            height: 18,
            borderRadius: '4px',
            border: `1.5px solid ${checked ? wf.blue : '#b8b8c8'}`,
            background: checked ? wf.blue : wf.surface,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {checked && (
            <Box
              component="span"
              sx={{
                width: 5,
                height: 9,
                borderRight: '2px solid #fff',
                borderBottom: '2px solid #fff',
                transform: 'translateY(-1px) rotate(45deg)',
              }}
            />
          )}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        {role === 'checkbox' && (
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ alignItems: 'baseline', flexWrap: 'wrap', rowGap: 0.25 }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
            {note && <Typography sx={{ fontSize: 12, color: wf.meta }}>{note}</Typography>}
            {chip && (
              <Box component="span" sx={pillSx('slate')}>
                {chip}
              </Box>
            )}
          </Stack>
        )}
        <Typography sx={{ fontSize: 13, color: '#52526a', lineHeight: 1.6, mt: '4px' }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

/** Presentation only. Persistence and the continue action belong to the owner. */
export function IntakeSetupServicesPage({
  tier,
  onTierChange,
  services,
  onServicesChange,
  canManage,
  isSaving = false,
  onContinue,
  continueDisabled = false,
  signedLocked = false,
}: {
  tier: IntakeServiceTier;
  onTierChange: (tier: IntakeServiceTier) => void;
  services: IntakeSetupService[];
  onServicesChange: (services: IntakeSetupService[]) => void;
  canManage: boolean;
  isSaving?: boolean;
  onContinue: () => void;
  continueDisabled?: boolean;
  /** The practice has signed: the choices are part of that agreement and read-only. */
  signedLocked?: boolean;
}) {
  const locked = !canManage || isSaving || signedLocked;
  const toggle = (id: IntakeSetupService) => {
    let next = services.includes(id) ? services.filter(s => s !== id) : [...services, id];
    // Dropping a service also drops whatever required it.
    next = next.filter(
      picked =>
        !INTAKE_SETUP_SERVICES.find(s => s.id === picked)?.requires ||
        next.includes(
          INTAKE_SETUP_SERVICES.find(s => s.id === picked)?.requires as IntakeSetupService,
        ),
    );
    onServicesChange(next);
  };
  const selfPay = tier === 'self_pay';
  const plan = describeServicePlan(tier, services);
  const agreements = plan.hasServices ? agreementTitlesForPlan(plan) : [];
  const options = selfPay
    ? [{ ...SELF_PAY_ADDON, unmet: false }]
    : INTAKE_SETUP_SERVICES.map(service => ({
        ...service,
        chip: undefined as string | undefined,
        unmet: Boolean(service.requires && !services.includes(service.requires)),
      }));
  return (
    <Stack spacing={3}>
      <Box>
        <Typography
          component="h2"
          sx={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em', mb: '6px' }}
        >
          Which services are you signing up for?
        </Typography>
        <Typography sx={{ fontSize: 14, color: wf.muted, lineHeight: 1.6, maxWidth: 620 }}>
          Your selection determines the agreements your practice needs to sign. Changing services
          may require updated agreements.
        </Typography>
      </Box>

      {signedLocked && (
        <Alert severity="info">
          Your practice has signed its agreements, so these choices can no longer be changed here.
          Contact JotPsych support to make a change.
        </Alert>
      )}

      <Box
        role="radiogroup"
        aria-label="Service tier"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '12px',
        }}
      >
        {tiers.map(option => (
          <OptionCard
            key={option.id}
            role="radio"
            checked={tier === option.id}
            disabled={locked}
            title={INTAKE_SERVICE_TIER_LABELS[option.id]}
            description={option.description}
            onActivate={() => onTierChange(option.id)}
          />
        ))}
      </Box>

      <Stack spacing={1.5} role="group" aria-labelledby="intake-setup-services-heading">
        <Typography
          component="div"
          id="intake-setup-services-heading"
          sx={{ ...overlineSx, letterSpacing: '0.07em', color: wf.muted }}
        >
          {selfPay ? 'Optional add-on' : 'Selected services · Select all that apply'}
        </Typography>
        {options.map(service => (
          <OptionCard
            key={service.id}
            role="checkbox"
            checked={services.includes(service.id)}
            disabled={locked || service.unmet}
            title={service.title}
            description={service.description}
            note={service.note}
            chip={service.chip}
            onActivate={() => toggle(service.id)}
          />
        ))}
      </Stack>

      <Box component="aside" aria-label="You'll sign" sx={{ ...cardSx, padding: '14px 18px' }}>
        <Stack
          direction="row"
          spacing="18px"
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
            <Typography component="div" sx={{ ...overlineSx, letterSpacing: '0.07em' }}>
              You&apos;ll sign
            </Typography>
            {agreements.length > 0 ? (
              <Typography sx={{ fontSize: 13, color: wf.body, mt: '4px', lineHeight: 1.6 }}>
                {agreements.join(' · ')}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 13, color: wf.muted, mt: '4px', lineHeight: 1.6 }}>
                Select at least one service to see your agreements.
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: wf.blue, flexShrink: 0 }}>
            {plan.hasServices ? plan.name : INTAKE_SERVICE_TIER_LABELS[tier]}
          </Typography>
        </Stack>
      </Box>

      <Box>
        <Button
          variant="contained"
          endIcon={<ArrowForwardOutlinedIcon />}
          disabled={signedLocked ? isSaving : locked || continueDisabled}
          onClick={onContinue}
        >
          Continue to agreements
        </Button>
      </Box>
    </Stack>
  );
}
